/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Link as ChakraLink} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as SPALink, NavLink as NavSPALink} from 'react-router-dom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const isAbsolute = (href) => {
    return href?.indexOf('https://') === 0 || href?.indexOf('http://') === 0
}

const isFunctionalLink = (href) => {
    return href?.indexOf('mailto:') === 0 || href?.indexOf('tel:') === 0
}

const Link = React.forwardRef(({href, to, useNavLink = false, ...props}, ref) => {
    const _href = to || href
    const {buildUrl} = useMultiSite()
    const absolute = isAbsolute(_href)
    const updatedHref = absolute ? _href : buildUrl(_href)
    return isFunctionalLink(_href) ? (
        <ChakraLink {...props} href={_href} ref={ref} />
    ) : absolute ? (
        <ChakraLink {...props} href={updatedHref} ref={ref} isExternal />
    ) : (
        <ChakraLink
            as={useNavLink ? NavSPALink : SPALink}
            {...(useNavLink && {exact: true})}
            {...props}
            to={updatedHref}
            ref={ref}
        />
    )
})

Link.displayName = 'Link'

Link.propTypes = {href: PropTypes.string, to: PropTypes.string, useNavLink: PropTypes.bool}

export default React.memo(Link)
